// Chronological activity feed for /tickets/feed.
//
// Merges two streams into a single time-sorted list:
//   1. public.agent_activity_log — agent ship/pick/block/ask-user events
//   2. public.tickets — most recently updated tickets (any assignee)
//
// Returns flat array of feed entries shaped as:
//   {
//     id,                  // stable key for DOMQL keyed render
//     kind,                // 'shipped' | 'picked_up' | 'blocked' | ...
//                          //   | 'ticket_update'   (fallback kind from tickets table)
//     tsISO,               // event time (agent_activity_log.created_at or tickets.updated_at)
//     actorEmail,          // agent_name if activity log; assignee_email otherwise
//     ticketId,            // when present
//     ticketExternalId,
//     ticketTitle,
//     ticketType,
//     ticketState,
//     commitSha,
//     note
//   }

// Phase 7c-bis (2026-05-19): tickets migrated to sdk.tickets.list.
// agent_activity_log migrated off Supabase REST to /core/agent-activity
// (Mongo) on 2026-07-03; Supabase is fully retired, so this is Mongo-only.

// coreHeaders / workspaceApiBase / getSDK come from @symbo-ls/workspace-shared,
// stubbed by frank's EXTERNAL_PACKAGES at serialization time — reached via
// el.call instead of raw imports (see TicketDetailModal.js for the full
// rationale).

const WORKSPACE_ID = '69e5911201b0ef47b675463f'

// Named _activityFeedUrl (not _activityUrl) — fetchGeneralTriage.js declares
// its OWN, differently-shaped `_activityUrl(el, wsId)` module-scope const.
// Two same-named private helpers across bundled files silently confused
// frank's globalScope promotion in the past (see GeneralMain.js's
// _approveTicket → _approveTicketRow rename, same fix) — a unique name
// removes the collision outright.
const _activityFeedUrl = (el, limit) =>
  `${el.call('workspaceApiBase')}/core/agent-activity?workspace=${WORKSPACE_ID}&limit=${limit}`

export const fetchActivityFeed = async (el, { limit = 50 } = {}) => {
  const [logs, tickets] = await Promise.all([
    // agent_activity_log — Mongo, via /core/agent-activity.
    fetch(_activityFeedUrl(el, limit), { headers: el.call('coreHeaders') })
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => []),

    // tickets → sdk.tickets.list (Mongo-backed).
    (async () => {
      try {
        const tSvc = await el.call('getSDK').getService('tickets')
        const result = await tSvc.list(
          { deleted: false },
          { sortBy: 'updatedAt', sortDir: 'desc', limit }
        )
        return Array.isArray(result) ? result : result.data || []
      } catch (_) {
        return []
      }
    })()
  ])

  // Build ticket lookup so we can enrich activity log rows.
  // Mongo docs use _id; dual-key for safety.
  const tById = new Map()
  for (const t of tickets) {
    if (t._id) tById.set(t._id, t)
    if (t.id) tById.set(t.id, t)
  }

  const feed = []

  for (const l of logs) {
    const t = l.ticket_id ? tById.get(l.ticket_id) : null
    feed.push({
      id: `log:${l.id}`,
      kind: l.kind,
      tsISO: l.created_at,
      actorEmail: l.agent_name ? `${l.agent_name.toLowerCase()}-agent@symbols.app` : null,
      actorDisplay: l.agent_name || 'agent',
      ticketId: l.ticket_id,
      ticketExternalId: t ? t.externalId || t.external_id : null,
      ticketTitle: t?.title,
      ticketType: t?.type,
      ticketState: t?.state,
      commitSha: l.commit_sha,
      note: l.note
    })
  }

  // Fold in ticket updates that don't have a matching activity log entry
  // (human edits, external syncs, etc.) so humans aren't invisible.
  const loggedTicketIds = new Set(logs.filter((l) => l.ticket_id).map((l) => l.ticket_id))
  for (const t of tickets) {
    const tid = t._id || t.id
    if (!tid || loggedTicketIds.has(tid)) continue
    const assigneeEmail = t.assigneeEmail || t.assignee_email
    const reporterEmail = t.reporterEmail || t.reporter_email
    const createdAt = t.createdAt || t.created_at
    const updatedAt = t.updatedAt || t.updated_at
    // A freshly created ticket has updatedAt === createdAt (the create write
    // stamps both). Previously such tickets surfaced as "updated by <assignee>"
    // — a spurious 'updated' entry on creation, misattributed to the assignee
    // instead of the creator. Detect the just-created case (timestamps within a
    // 2s slack to absorb create/persist clock skew) and emit a 'ticket_created'
    // entry attributed to the REPORTER. Edited tickets keep 'ticket_update'.
    const ct = createdAt ? new Date(createdAt).getTime() : 0
    const ut = updatedAt ? new Date(updatedAt).getTime() : 0
    const isJustCreated = ct && ut && Math.abs(ut - ct) <= 2000
    const actorEmail = isJustCreated ? reporterEmail || assigneeEmail : assigneeEmail
    feed.push({
      id: `ticket:${tid}`,
      kind: isJustCreated ? 'ticket_created' : 'ticket_update',
      tsISO: updatedAt,
      actorEmail,
      actorDisplay: actorEmail?.split('@')[0] || 'someone',
      ticketId: tid,
      ticketExternalId: t.externalId || t.external_id,
      ticketTitle: t.title,
      ticketType: t.type,
      ticketState: t.state,
      commitSha: null,
      note: null
    })
  }

  feed.sort((a, b) => new Date(b.tsISO || 0) - new Date(a.tsISO || 0))
  return feed.slice(0, limit)
}
